import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Container, Grid } from '@material-ui/core';
import { Select } from '@michelin/acid-components';
import { CentralProviderContext, CentralProviderState, Languages, useTranslation } from '@michelin/central-provider';
import { SelectedTire } from 'compositions/Wizard/WizardContext';
import hash from 'object-hash';
import { UFOPart, VehicleType } from 'types/UFOTypes';
import { TIRE_DEFAULT_QTY } from 'utils/constantValues';
import { longDate } from 'utils/format';
import { getMspn } from 'utils/getMspn';
import { CommonPricesParamsType } from 'utils/getPricesCallCommonParams';
import { BrandImage } from '../../OrderDetail/components/BrandImage';
import { FitWarning } from './FitWarning';
import Pricing from './Pricing';

interface WizardTireInformationDesktopProps {
  tire: UFOPart;
  vehicle?: VehicleType;
  fittingTires: string[];
  commonPricesParams: CommonPricesParamsType;
  isLumped?: boolean;
  selectedTires: SelectedTire[];
  setSelectedTires: React.Dispatch<React.SetStateAction<SelectedTire[]>>;
}
export default function WizardTireInformationDesktop(props: WizardTireInformationDesktopProps) {
  const { tire, fittingTires, commonPricesParams, isLumped, vehicle, selectedTires, setSelectedTires } = props;
  const { t } = useTranslation();
  const [qty, setQty] = useState<number>(TIRE_DEFAULT_QTY);
  const [tireIsSelected, setTireIsSelected] = useState(false);
  const [selectedOptionTire, setSelectedOptionTire] = useState(tire);
  const { currentLanguage } = useContext<CentralProviderState>(CentralProviderContext);

  const {
    image_url: imageUrl,
    size,
    mspn,
    dealer_part_id: dealerPartId,
    estimatedDelivery,
    mktInfo,
    warranty,
    manufacturer,
    brand_name: brandName,
    tire_title: tireTitle = '',
  } = isLumped ? tire : selectedOptionTire;

  useEffect(() => {
    if (!isLumped && selectedTires) {
      const foundedTire = selectedTires.find((st) => st.part.mspn === getMspn(tire) && !st.removed);
      setTireIsSelected(!!foundedTire);
      if (foundedTire && foundedTire.qty !== qty) {
        setQty(foundedTire.qty);
      }
    }
    if (isLumped && selectedTires) {
      const foundedTire = selectedTires.find((st) => st.part.mspn === getMspn(selectedOptionTire) && !st.removed);
      setTireIsSelected(!!foundedTire);
      if (foundedTire && foundedTire.qty !== qty) {
        setQty(foundedTire.qty);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash(selectedTires || [])]);

  useEffect(() => {
    if (!isLumped && tire && tireIsSelected) {
      setSelectedTires((prevState) =>
        prevState.map((st) => {
          if (st.part.mspn === getMspn(tire)) {
            return { ...st, qty };
          }
          return st;
        }),
      );
    }
    if (isLumped && selectedOptionTire && tireIsSelected) {
      setSelectedTires((prevState) =>
        prevState.map((st) => {
          if (st.part.mspn === getMspn(selectedOptionTire)) {
            return { ...st, qty };
          }
          return st;
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty]);

  const getPartNumberLabel = (_tire: UFOPart) => {
    let partNumberLabel = `${t('Part #: ')}${_tire.mspn}`;
    if (_tire.dealer_part_id) partNumberLabel += `|${_tire.dealer_part_id}`;
    return partNumberLabel;
  };

  const tiresOptions = [
    {
      label: `${tire.size} - (${getPartNumberLabel(tire)})`,
      value: tire,
    },
  ];

  tire.child?.forEach((childTire) => {
    tiresOptions.push({
      label: `${childTire.size} - (${getPartNumberLabel(childTire)})`,
      value: childTire,
    });
  });

  const benefits = useMemo(() => {
    const acceleratorGhostedBenefits = mktInfo?.acceleratorGhostedBenefits
    return currentLanguage === Languages.FR_CA && acceleratorGhostedBenefits?.fr && acceleratorGhostedBenefits.fr.length > 0 ? acceleratorGhostedBenefits.fr : acceleratorGhostedBenefits?.en
  }, [currentLanguage, mktInfo]);

  const isImage = imageUrl && imageUrl.length > 0;

  return (
    <>
      <Grid container spacing={0} style={{ height: 250, position: 'relative' }}>
        {imageUrl && imageUrl.length > 0 && (
          <Grid item xs={2}>
            <div style={{ marginLeft: -8, marginRight: 16 }}>
              <BrandImage imageName={imageUrl} />
            </div>
          </Grid>
        )}
        <Grid item xs={isImage ? 6 : 8}>
          {!isLumped && (
            <Container>
              <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                {`${(brandName || manufacturer) ?? ''} - ${tireTitle}`}
                <br />
              </span>
              <span style={{ fontSize: 12 }}>
                {size}
                <br />
                <span id="tirePartNumber">
                  <b>{t('Part #: ')}</b>
                  {mspn}
                </span>
                {dealerPartId && <>{`|${dealerPartId}`}</>}
              </span>
            </Container>
          )}
          {isLumped && (
            <Container>
              <span style={{ fontSize: 16, fontWeight: 'bold' }}>
                {`${(brandName || manufacturer) ?? ''} - ${tireTitle}`}
                <br />
              </span>
              <Select
                style={{ width: '100%' }}
                value={selectedOptionTire}
                onChange={(e) => {
                  setSelectedOptionTire(e.target.value as UFOPart);
                }}
                margin="dense"
                options={tiresOptions}
              />
              <br />
              <span style={{ fontSize: 12 }}>
                {size}
                <br />
                <span id="lumpedTirePartNumber">
                  <b>{t('Part #: ')}</b>
                  {mspn}
                </span>
                {dealerPartId && <>{`|${dealerPartId}`}</>}
              </span>
            </Container>
          )}
          <Container style={{ margin: 16 }}>
            <span style={{ fontSize: 12 }}>
              {benefits?.map((f) => (
                <div key={f}>{`● ${t(f)}`}</div>
              ))}
              {warranty !== 'Standard' && <div>{`● ${t('Mileage warranty: {{miles}}', { miles: warranty })}`}</div>}
            </span>
          </Container>
          <FitWarning vehicle={vehicle} fit={fittingTires.includes(tire.mspn)} />
        </Grid>
        <Grid item xs={4} container direction="column" alignItems="flex-end" justify="flex-start">
          <Pricing
            qty={qty}
            setQty={(q) => setQty(q)}
            tireIsSelected={tireIsSelected}
            tire={isLumped ? selectedOptionTire : tire}
            selectedTires={selectedTires}
            setSelectedTires={setSelectedTires}
            commonPricesParams={commonPricesParams}
            stockingOrder={!vehicle}
          />
          {estimatedDelivery && (
            <>
              {t('Estimated delivery ')}
              <b>{longDate(estimatedDelivery)}</b>
              <br />
            </>
          )}
        </Grid>
      </Grid>
    </>
  );
}
